'use client'

import * as React from 'react'
import { ReactElement, useEffect } from 'react'
import { TextField } from '@repo/ui/components/forms/text-field'
import { Controller, useForm } from 'react-hook-form'
import { Checkbox } from '@repo/ui'
import ButtonCustom from '@/components/button-custom'
import useBasePath from '@/hooks/use-base-path'
import { useRouter } from 'next/navigation'
import { useCustomerStore } from '@/hooks/use-customer/store'
import {
  addressSelector,
  customerSelector,
  setEmailSelector,
} from '@/hooks/use-customer/selectors'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import { existUser } from '@/actions/user'
import { platformUrl } from '@repo/account/platform-url'
import {
  productFieldsSelector,
  ProductStoreFields,
} from '@/hooks/use-cart-product/selectors'
import { sendGAEvent } from '@/utils/gtm-events'
import { useProductStore } from '@/hooks/use-cart-product/store'
import { useTrackingStore } from '@/hooks/use-tracking/store'
import { currentStepSelector } from '@/hooks/use-tracking/selectors'
import { pathFunnel } from '@/app/payment/[academy]/[product]/(funnel)/step-path-names'
import { isFreemiumUserByEmail } from '@/actions/freemium'
import {
  getFeatureFlag,
  reloadFlags,
  setPersonProperties,
} from '@repo/tracking/posthog'

const AB_FLAG = 'ck-habit-study'

const createFormSchema = z.object({
  email: z.string().email('Email no válido'),
  terms: z.literal<boolean>(true),
})
type CreateFormUser = z.infer<typeof createFormSchema>

const extraClassNameLabel = 'px-0 text-checkout-s font-normal'
const extraClassName =
  'text-base bg-white font-normal border-2 border-[#CCCCCC]'
const alreadyExistUserError = `Hemos encontrado una cuenta <b>activa</b> con ese correo electrónico. Utiliza un correo electrónico diferente o prueba a <a style="text-decoration: underline" href="${platformUrl()}/login">iniciar sesión.</a>`

export function FormUser(): ReactElement {
  const [statusUserForm, setStatusUser] = React.useState<{
    status: 'initial' | 'processing' | 'error'
  }>({ status: 'initial' })

  const setEmail = useCustomerStore(setEmailSelector)
  const productStore: ProductStoreFields = useProductStore(
    productFieldsSelector
  )
  const customerStore = useCustomerStore(customerSelector)
  const addressStore = useCustomerStore(addressSelector)
  const trackingStore = useTrackingStore(currentStepSelector)
  const path = useBasePath()
  const router = useRouter()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setError,
  } = useForm<CreateFormUser, any>({
    resolver: zodResolver(createFormSchema),
    defaultValues: {
      terms: false,
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  // Load feature flags for anonymous user on mount
  useEffect(() => {
    reloadFlags()
  }, [])

  useEffect(() => {
    setTimeout(() => {
      if (productStore?.currentProducts) {
        sendGAEvent(
          'add_to_cart',
          productStore,
          trackingStore?.currentStep?.academy,
          customerStore,
          addressStore
        )
      }
    }, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productStore])

  const onSubmit = async (data: CreateFormUser) => {
    try {
      if (statusUserForm.status === 'processing') return

      setStatusUser({ status: 'processing' })

      const userExists = await existUser(data.email)

      // Check if user is freemium
      const isFreemium = userExists
        ? await isFreemiumUserByEmail(data.email)
        : false

      // Allow purchase if:
      // 1. Product allows students (canBeStudent), OR
      // 2. User doesn't exist, OR
      // 3. User exists but is freemium
      if (
        !productStore.currentProducts.canBeStudent &&
        userExists &&
        !isFreemium
      ) {
        setStatusUser({ status: 'error' })
        setError('email', { message: alreadyExistUserError })
      } else {
        // Persist SDK flag (anonymous) as person + super property
        const variant = getFeatureFlag(AB_FLAG)
        if (typeof variant === 'string') {
          setPersonProperties({ email: data.email, [AB_FLAG]: variant })
        }
        setEmail(data.email.toLowerCase())
        sendGAEvent(
          'begin_checkout',
          productStore,
          trackingStore?.currentStep?.academy,
          { ...customerStore, email: data.email },
          addressStore
        )
        router.push(`${path}/${pathFunnel.fromStep1}`)
      }
    } catch (err) {
      const { message } = err as Error

      setStatusUser({ status: 'error' })
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset className="flex flex-col gap-4">
          <TextField
            name={'email'}
            type={'email'}
            label={'Email'}
            placeholder={'Tu correo electrónico'}
            variant={'secondary'}
            {...register('email')}
            required={true}
            className={extraClassName}
            labelClassName={extraClassNameLabel}
            error={errors.email?.message}
          />

          <div className="flex flex-col">
            <div className="flex flex-row items-center space-x-4">
              <Controller
                name="terms"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    name="terms"
                    defaultChecked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <p className="text-checkout-s font-medium text-[#12131680]">
                Acepto la{' '}
                <Link
                  className="underline"
                  href={
                    'https://formacion.ninja/aviso-legal/politica-de-privacidad'
                  }
                  target={'_blank'}>
                  Política de Privacidad
                </Link>{' '}
                y las{' '}
                <Link
                  className="underline"
                  href={
                    'https://formacion.ninja/aviso-legal/condiciones-de-contratacion'
                  }
                  target={'_blank'}>
                  Condiciones de contratación
                </Link>
                .
              </p>
            </div>
            <div className="h-[17px]">
              {' '}
              {errors?.terms && (
                <div className="flex items-center gap-2">
                  <p className="p-1 pl-0 text-[14px] font-medium text-[#DF1B41]">
                    {'Obligatorio aceptar términos y condiciones'}
                  </p>
                </div>
              )}
            </div>
          </div>
          <ButtonCustom
            loading={statusUserForm.status === 'processing'}
            type="submit"
            className="disabled:bg-checkout-grey-500 mt-3 min-w-full py-3">
            Continuar
          </ButtonCustom>
        </fieldset>
      </form>
    </>
  )
}
